import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/pages/dashboard/_shared";
import { Map as MapIcon, RefreshCw, AlertCircle, ExternalLink, Truck } from "lucide-react";

// Build a custom marker so we don't depend on Leaflet's broken
// webpack-default icon paths. Orange when moving, grey when idle/no
// ignition. Includes the rego right on the pin so the office can
// glance at the map and know which vehicle is which.
function buildIcon(rego, moving) {
  const colour = moving ? "#FF5F15" : "#71717A";
  const html = `
    <div style="
      display:flex; align-items:center; gap:4px;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-weight: 700; font-size: 11px;
      pointer-events: none;
      transform: translate(-12px, -12px);
    ">
      <div style="
        width:14px; height:14px; border-radius:50%;
        background:${colour};
        border: 3px solid #fff;
        box-shadow: 0 0 0 1px rgba(0,0,0,.35);
      "></div>
      <div style="
        background:#09090B; color:#fff; padding:1px 5px;
        white-space:nowrap; letter-spacing:.05em;
        box-shadow: 0 1px 2px rgba(0,0,0,.4);
      ">${rego}</div>
    </div>`;
  return L.divIcon({
    html,
    className: "fleet-marker",  // strips Leaflet's default white box
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

// Helper component to re-fit the map bounds whenever the visible
// vehicle list changes (e.g. after a poll). Skipped on the first
// render to avoid yanking the user's view if they've panned.
function FitBounds({ points }) {
  const map = useMap();
  const firstFitRef = useRef(false);
  useEffect(() => {
    if (!points.length) return;
    if (firstFitRef.current) return;
    firstFitRef.current = true;
    try {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    } catch {}
  }, [points, map]);
  return null;
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return v; }
}
function fmtNum(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
}

function normaliseRow(s) {
  // Cartrack /vehicles/status nests lat/lng inside .location
  const loc = s.location || {};
  const lat = loc.latitude ?? s.latitude ?? s.lat;
  const lng = loc.longitude ?? s.longitude ?? s.lon ?? s.lng;
  return {
    id: s.vehicle_id || s.id || s.registration,
    rego: (s.registration || s.rego || "—").toUpperCase(),
    lat: typeof lat === "number" ? lat : (lat ? Number(lat) : null),
    lng: typeof lng === "number" ? lng : (lng ? Number(lng) : null),
    speed: s.speed,
    odometer: s.odometer,
    fuel: s.fuel?.level ?? s.fuel_level,
    ignition: s.ignition,
    bearing: s.bearing,
    address: loc.position_description || s.address,
    last_seen: s.event_ts || loc.updated || s.timestamp,
  };
}

export function CartrackLiveMap({ provider = "cartrack" } = {}) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  // EROAD's usage policy forbids polling faster than once per 60s; Cartrack is fine at 15s.
  const pollMs = provider === "eroad" ? 60000 : 15000;

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get("/tracking/vehicles/status");
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setRows(list.map(normaliseRow));
    } catch (e) {
      setErr(e);
      if (e.response?.status === 429) setPaused(true);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    load();
    if (paused) return;
    timer.current = setInterval(load, pollMs);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line
  }, [paused, pollMs]);

  const onMap = useMemo(() => rows.filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lng)), [rows]);
  const offline = useMemo(() => rows.filter((r) => !Number.isFinite(r.lat) || !Number.isFinite(r.lng)), [rows]);

  // Default to roughly the middle of NZ until we get real positions
  const fallbackCentre = [-41.0, 174.0];
  const centre = onMap.length ? [onMap[0].lat, onMap[0].lng] : fallbackCentre;

  if (err && !rows.length) {
    return (
      <div className="border-2 border-red-600 bg-red-50 dark:bg-red-900/10 p-4" data-testid="ct-livemap-err">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 dark:text-red-400">Tracking call failed</div>
            <div className="text-sm font-bold mt-0.5 break-words">{err?.response?.data?.detail || err?.message || "Unknown error"}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ct-livemap-tab" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {onMap.length} on map · {offline.length} offline · {paused ? "Paused" : `Polling every ${Math.round(pollMs / 1000)}s`}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setPaused((p) => !p)} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="ct-livemap-pause">
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold text-xs">
            <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="border-2 border-zinc-900 dark:border-zinc-700 overflow-hidden" style={{ height: 540 }} data-testid="ct-livemap">
        <MapContainer center={centre} zoom={onMap.length ? 10 : 5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" referrerPolicy="strict-origin-when-cross-origin"
          />
          <FitBounds points={onMap} />
          {onMap.map((v) => {
            const moving = (v.speed != null && Number(v.speed) > 2);
            return (
              <Marker key={v.id} position={[v.lat, v.lng]} icon={buildIcon(v.rego, moving)} data-testid={`ct-marker-${v.rego}`}>
                <Popup>
                  <div className="font-mono text-xs space-y-1" style={{ minWidth: 200 }}>
                    <div className="font-bold uppercase tracking-wider">{v.rego}</div>
                    {v.address && <div className="break-words">{v.address}</div>}
                    {v.speed != null && <div>Speed: <b>{fmtNum(v.speed)} km/h</b> {moving ? "· moving" : "· stopped"}</div>}
                    {v.odometer != null && <div>Odo: <b>{fmtNum(Math.round(v.odometer))} km</b></div>}
                    {v.ignition != null && <div>Ignition: <b>{v.ignition ? "ON" : "OFF"}</b></div>}
                    {v.last_seen && <div className="text-zinc-500 text-[10px] uppercase tracking-widest">{fmtDate(v.last_seen)}</div>}
                    <a
                      href={`https://www.google.com/maps?q=${v.lat},${v.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[#FF5F15] hover:underline"
                    >
                      Open in Google Maps <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {offline.length > 0 && (
        <div className="border-2 border-zinc-300 dark:border-zinc-700 bg-white dark:bg-[#151C27] p-3" data-testid="ct-livemap-offline">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Offline / no GPS fix ({offline.length})</div>
          <div className="flex flex-wrap gap-2">
            {offline.map((v) => (
              <span key={v.id} className="inline-flex items-center gap-1.5 border border-zinc-300 dark:border-zinc-700 px-2 py-1 text-[10px] font-mono uppercase tracking-widest">
                <Truck className="w-3 h-3" /> {v.rego}
                {v.last_seen && <span className="text-zinc-500">· {fmtDate(v.last_seen)}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {onMap.length === 0 && offline.length === 0 && !busy && (
        <EmptyState icon={MapIcon} title="No live data" subtitle="No vehicle positions have come through yet." />
      )}
    </div>
  );
}

export default CartrackLiveMap;
